import type { getRevenuesParams } from 'api/v2/revenues';
import type { getExpensesParams } from 'api/v3/expenses';
import { stringify } from 'query-string';
import type { ExportEntry } from 'types/export';
import type { ExportListType, RetryExportType } from 'types/global.types';
import { apiGetWithExpertContext } from 'utils/apiWithWorkspaceHeader';
import api from '../../axiosInstance';

export const getExportById = (exportId: string): Promise<ExportEntry> => {
  return api.get(`/v2/exports/${exportId}`);
};

/**
 * GET export status with per-request x-on-behalf-of (x-workspace from axios default).
 */
export const getExportByIdForCustomer = (
  exportId: string,
  customerId: string,
): Promise<ExportEntry> =>
  apiGetWithExpertContext<ExportEntry>(`/v2/exports/${exportId}`, {
    customerId,
  });

const toClassifyMapper = {
  yes: 'true',
  no: 'false',
};

const hiddenTransactionsFilterMapper = {
  yes: 'true',
  no: 'false',
};

export const exportExpenses = (
  query: getExpensesParams,
  body: ExportListType,
): Promise<any> =>
  api.post(
    `/v2/exports/expenses?${stringify(query, {
      skipEmptyString: true,
      arrayFormat: 'comma',
    })}`,
    body,
  );

export const retryFailedExpenses = (
  expenseIds: string[],
  body: RetryExportType,
): Promise<{ exportId: string }> =>
  api.post(
    `/v2/exports/expenses?${stringify(
      { ids: expenseIds },
      {
        skipEmptyString: true,
        arrayFormat: 'comma',
      },
    )}`,
    body,
  );

export const exportRepoDocuments = (
  query: Record<string, any>,
  body: Omit<ExportListType, 'integrationId'>,
): Promise<any> =>
  api.post(
    `/v2/exports/repository-documents?${stringify(query, {
      skipEmptyString: true,
      arrayFormat: 'comma',
    })}`,
    body,
  );

export const exportRevenues = (
  query: getRevenuesParams,
  body: ExportListType,
): Promise<any> =>
  api.post(
    `/v2/exports/revenues?${stringify(query, {
      skipEmptyString: true,
      arrayFormat: 'comma',
    })}`,
    body,
  );

export const retryFailedRevenues = (
  revenueIds: string[],
  body: RetryExportType,
): Promise<{ exportId: string }> =>
  api.post(
    `/v2/exports/revenues?${stringify(
      { ids: revenueIds },
      {
        skipEmptyString: true,
        arrayFormat: 'comma',
      },
    )}`,
    body,
  );

export const exportTransactions = (
  query: any,
  body: ExportListType,
): Promise<any> =>
  api.post(
    `/v2/exports/transactions?${stringify(
      {
        ...query,
        toClassify:
          toClassifyMapper[query.toClassify as keyof typeof toClassifyMapper],
        deleted:
          hiddenTransactionsFilterMapper[
            query.deleted as keyof typeof hiddenTransactionsFilterMapper
          ],
      },
      {
        skipEmptyString: true,
        arrayFormat: 'comma',
      },
    )}`,
    body,
  );

export const retryFailedTransactions = (
  transactionIds: string[],
  body: RetryExportType,
): Promise<{ exportId: string }> =>
  api.post(
    `/v2/exports/transactions?${stringify(
      { ids: transactionIds },
      {
        skipEmptyString: true,
        arrayFormat: 'comma',
      },
    )}`,
    body,
  );

export const getExportHistory = () => api.get('/v2/exports');

export const updateExportDocumentsStatus = (
  exportId: string,
  documentIds: string[],
  action: 'resolve' | 'unresolve',
): Promise<any> =>
  api.patch(`/v2/exports/${exportId}/documents/status`, {
    ids: documentIds,
    action,
  });
